import React from "react";
import WatermarkedMedia from "./WatermarkedMedia";
import Editable from "./Editable";
import BrandName from "./BrandName";

export const Regional = ({ data = {}, editable, onChange, onMediaClick }) => {
  const setField = (k, v) => onChange && onChange({ ...data, [k]: v });
  const langs = data.languages || [];
  const setLang = (i, key, v) => {
    const next = langs.map((l, idx) => (idx === i ? { ...l, [key]: v } : l));
    setField("languages", next);
  };

  return (
    <section
      id="regional"
      className="relative bg-onyx-800 py-24 md:py-32 overflow-hidden"
      data-testid="regional-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-gold" />
            <Editable
              editable={editable}
              value={data.eyebrow}
              onChange={(v) => setField("eyebrow", v)}
              className="font-cinzel text-[11px] tracking-[0.4em] uppercase text-gold"
              testId="regional-eyebrow"
            />
          </div>
          <Editable
            as="h2"
            editable={editable}
            value={data.title}
            onChange={(v) => setField("title", v)}
            className="font-bebas text-5xl md:text-6xl lg:text-7xl text-ivory leading-[0.95]"
            testId="regional-title"
          />
          <Editable
            as="p"
            multiline
            editable={editable}
            value={data.body}
            onChange={(v) => setField("body", v)}
            className="mt-7 text-ivory/70 leading-relaxed text-base md:text-lg max-w-2xl"
            testId="regional-body"
          />

          <div className="mt-10 grid grid-cols-2 gap-px bg-gold/15 max-w-2xl">
            {langs.map((l, i) => (
              <div
                key={l.name || `lang-${i}`}
                className="bg-onyx-800 hover:bg-onyx-700 p-6 transition-colors group"
                data-testid={`regional-lang-${i}`}
              >
                <Editable
                  editable={editable}
                  value={l.name}
                  onChange={(v) => setLang(i, "name", v)}
                  className="font-bebas text-3xl md:text-4xl text-gold-gradient block"
                />
                <Editable
                  editable={editable}
                  value={l.tagline}
                  onChange={(v) => setLang(i, "tagline", v)}
                  className="mt-2 text-xs tracking-wider text-ivory/60 uppercase block"
                />
                <div className="mt-3 h-px w-8 bg-gold/40 transition-all duration-500 group-hover:w-16 group-hover:bg-gold" />
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <RegionPill region="🇮🇳 India" />
            <RegionPill region="🇬🇧 United Kingdom" />
            <RegionPill region="🇪🇺 Europe" />
          </div>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2 relative">
          <div className="relative aspect-[4/5] overflow-hidden">
            <WatermarkedMedia
              src={data.image_url}
              alt="Regional music video production"
              position="bottom-right"
              opacity={0.85}
              watermarkSize={56}
              showCaption={false}
              className="h-full w-full"
              testId="regional-image"
            />
            {editable && (
              <button
                onClick={onMediaClick}
                className="absolute top-4 left-4 font-cinzel text-[10px] tracking-[0.3em] uppercase text-onyx-900 bg-gold hover:bg-gold-bright px-4 py-2"
                data-testid="regional-edit-media-btn"
              >
                Replace Image
              </button>
            )}
          </div>
          <div className="absolute -bottom-6 -left-6 w-32 h-32 border border-gold/40 hidden md:block" />
          <div className="absolute -top-6 -right-6 hidden md:flex font-bebas text-7xl text-gold/30 select-none">
            <BrandName short />
          </div>
        </div>
      </div>
    </section>
  );
};

const RegionPill = ({ region }) => (
  <span className="font-bebas text-base tracking-wider px-4 py-2 border border-gold/40 text-ivory uppercase hover:border-gold hover:bg-gold/10 transition-colors">
    {region}
  </span>
);

export default Regional;
